// Libs
import clsx from "clsx";
import type React from "react";

// Utils
import { type RDivProps, alterElement } from "../../core/utils";

// Components
import { Icon, type IconProps, type Icons } from "../icons";
import { IconTooltip } from "../modals";

export interface CardTitleProps extends React.PropsWithChildren<RDivProps> {
    /**
     * Additional className for title
     */
    className?: string;
    /**
     * Extended padding top and bottom
     */
    extendedPadding?: boolean;
    /**
     * Helper icon to display
     */
    helperIcon?: Icons;
    /**
     * Additional className for helper Icon
     */
    helperIconClassName?: string;
    /**
     * Additional props for helper Icon
     */
    helperIconProps?: IconProps;
    /**
     * Helper text
     */
    helperText?: React.ReactNode;
    /**
     * Helper text title
     */
    helperTextTitle?: React.ReactNode;
    /**
     * Icon displayed on the title
     */
    icon?: Icons;
    /**
     * iconProps
     */
    iconProps?: IconProps;
    /**
     * iconClassName for the icon
     */
    iconClassName?: string;
    /**
     * Subtitle
     */
    subtitle?: string;
    /**
     * Title
     */
    title?: any;
    /**
     * Title additional className
     */
    titleClassName?: string;
}

export function CardTitle({
    children,
    className,
    extendedPadding = false,
    helperIcon = "info",
    helperIconClassName: helperIconClassNameProp,
    helperIconProps,
    helperText,
    helperTextTitle,
    icon,
    iconClassName: iconClassNameProps,
    iconProps,
    title,
    titleClassName: titleClassNameProp,
    subtitle,
    ...otherProps
}: CardTitleProps): JSX.Element {
    const rootClassName = clsx(
        "ds-c-card__title",
        {
            "ds-c-card__title--extended-padding": extendedPadding,
        },
        className
    );
    const helperIconClassName = clsx("ds-c-card__title-helper", helperIconClassNameProp);

    const titleClassName = clsx("ds-u-typography--subtitle-medium", titleClassNameProp);
    const iconClassName = clsx("ds-c-card__title-icon", iconClassNameProps);

    const wrappedChild = alterElement(title ?? children, { className: titleClassName });

    return (
        <div className={rootClassName} {...otherProps}>
            {icon && (
                <Icon className={iconClassName} size="s" {...iconProps}>
                    {icon}
                </Icon>
            )}

            <div className="ds-c-card__title-container">
                <div className="ds-c-card__title-title">
                    {wrappedChild}
                    {helperText && (
                        <IconTooltip
                            tooltip={helperText}
                            tooltipTitle={helperTextTitle}
                            iconSize="s"
                            icon={helperIcon}
                            iconProps={helperIconProps}
                            iconClassName={helperIconClassName}
                        />
                    )}
                </div>
                {subtitle && (
                    <div className="ds-c-card__title-subtext ds-u-text--subtext">{subtitle}</div>
                )}
            </div>
        </div>
    );
}
